import { useEffect, useState } from "react";
import {
  Container,
  Stack,
  Typography,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  SettingsRemote as SettingsRemoteIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import axios from "axios";

import { IStudent } from "./parts";

import {
  useDeleteStudents,
  useGetStudents,
  useImportStudentsCSV,
} from "api/management/students";
import {
  useGetBeacon,
  useAddBeacon,
  useUpdateBeacon,
} from "api/management/beacons";
import { useGetGrades } from "api/management/grades";
import { useGetSections } from "api/management/sections";

import { useNavigate } from "react-router-dom";

import { BeaconManageDialog, CSVImportDialog } from "./parts";
import {
  ConfirmationDialog,
  MessageDialog,
  ProgressBackdropDialog,
} from "components/parts/dialogs";
import { useI18n } from "i18n";

const Page = (props: any) => {
  const { t } = useI18n();
  const nav = useNavigate();
  const { loading: getLoading, service: getStudents } = useGetStudents();
  const { loading: importLoading, service: importStudentsCSV } =
    useImportStudentsCSV();

  const { loading: deleteLoading, service: deleteStudents } =
    useDeleteStudents();

  const { service: getGrades } = useGetGrades();
  const { service: getSections } = useGetSections();
  const [students, setStudents] = useState<IStudent[]>([]);

  const [openBeaconManageDialog, setOpenBeaconManageDialog] = useState(false);
  const [openCSVImportDialog, setOpenCSVImportDialog] = useState(false);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openNoticeDialog, setOpenNoticeDialog] = useState(false);
  const [noticeMessage, setNoticeMessage] = useState<string>("");

  const [beaconEditData, setBeaconEditData] = useState(null);
  const [beaconEditType, setBeaconEditType] = useState<"edit" | "add" | "none">(
    "none"
  );

  const { loading: getBeaconLoading, service: getBeacon } = useGetBeacon();
  const { loading: addBeaconLoading, service: addBeacon } = useAddBeacon();
  const { loading: updateBeaconLoading, service: updateBeacon } =
    useUpdateBeacon();

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const handleAddClick = () => {
    nav(`/management/students/detail`, { state: { mode: "add" } });
  };

  const handleDeleteClick = () => {
    if (rowSelectionModel.length === 0) {
      setNoticeMessage(t("students.selectToDelete"));
      setOpenNoticeDialog(true);
      return;
    }

    setOpenDeleteDialog(true);
  };
  const handleEditClick = (row) => {
    const id = row["id"];
    nav(`/management/students/detail`, { state: { mode: "edit", id: id } });
  };

  const handleManageBeaconClick = async (row) => {
    const { id: studentId } = row;
    const beaconResult = await getBeacon(studentId).then((response) => {
      const { data: result } = response;
      const { data } = result;
      return data;
    });

    const editData = {
      ...beaconResult,
      studentId: studentId,
    };

    setBeaconEditType(beaconResult ? "edit" : "add");
    setBeaconEditData(editData);
    setOpenBeaconManageDialog(true);
  };

  const handleCSVImportClick = () => {
    setOpenCSVImportDialog(true);
  };

  const handleCSVImport = async (csvData: any[]) => {
    try {
      const importResponse = await importStudentsCSV(csvData);
      // Refresh the students list after successful import
      const response = await getStudents();
      const { data: result } = response;
      const { data } = result;
      setStudents(data);
      return importResponse;
    } catch (error) {
      console.error("Failed to import CSV:", error);
      throw error;
    }
  };

  const handleGetGradeSections = async () => {
    try {
      const [gradesResponse, sectionsResponse] = await Promise.all([
        getGrades(),
        getSections(),
      ]);

      const grades =
        gradesResponse.data?.data?.map((grade: any) => grade.name) || [];
      const sections =
        sectionsResponse.data?.data?.map((section: any) => section.name) || [];

      return { grades, sections };
    } catch (error) {
      console.error("Failed to fetch grade/section data:", error);
      throw error;
    }
  };

  const columns: GridColDef[] = [
    { field: "id", headerName: t("field.id"), width: 90, hideable: false },
    {
      field: "firstName",
      headerName: t("field.firstName"),
      width: 150,
      editable: false,
    },
    {
      field: "lastName",
      headerName: t("field.lastName"),
      width: 150,
      editable: false,
    },
    {
      field: "gradeName",
      headerName: t("field.grade"),
      width: 150,
      editable: false,
    },
    {
      field: "sectionName",
      headerName: t("field.section"),
      width: 150,
      editable: false,
    },
    {
      field: "actions",
      headerName: t("common.actions"),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handleEditClick(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={() => handleManageBeaconClick(params.row)}
          >
            <SettingsRemoteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    getStudents()
      .then((response) => {
        const { data: result } = response;
        return result;
      })
      .then((result) => {
        const { data } = result;
        setStudents(data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.log(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center">
          <Typography
            variant="h1"
            gutterBottom
            sx={{ fontSize: "24px", fontWeight: "bold", mb: 4 }}
          >
            {t("students.title")}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={handleCSVImportClick}
            sx={{ mr: 1 }}
          >
            {t("students.importCsv")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{ mr: 1 }}
          >
            {t("common.add")}
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
          >
            {t("common.delete")}
          </Button>
        </Stack>
        <Box sx={{ height: 500, width: "100%" }}>
          <DataGrid
            loading={getLoading}
            rows={students}
            rowCount={students.length}
            columns={columns}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            rowSelectionModel={rowSelectionModel}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10,
                },
              },
              columns: {
                columnVisibilityModel: {
                  id: false,
                },
              },
            }}
            sx={{ minHeight: 400 }}
            pageSizeOptions={[10, 20, 50]}
            checkboxSelection
            disableRowSelectionOnClick
          />
        </Box>
      </Container>
      <ProgressBackdropDialog
        open={
          getLoading ||
          getBeaconLoading ||
          addBeaconLoading ||
          updateBeaconLoading ||
          importLoading
        }
      />
      <BeaconManageDialog
        open={openBeaconManageDialog}
        editType={beaconEditType}
        editData={beaconEditData}
        handleClose={() => {
          setOpenBeaconManageDialog(false);
        }}
        handleSubmit={(studentId, beaconId, data) => {
          if (beaconEditType === "edit") {
            return updateBeacon(studentId, beaconId, data);
          } else {
            return addBeacon(studentId, data);
          }
        }}
      />
      <CSVImportDialog
        open={openCSVImportDialog}
        onClose={() => setOpenCSVImportDialog(false)}
        onImport={handleCSVImport}
        onGetGradeSections={handleGetGradeSections}
      />

      <ConfirmationDialog
        title={t("students.deleteTitle")}
        message={t("students.deleteMessage")}
        negativeText={t("common.cancel")}
        positiveText={t("common.delete")}
        open={openDeleteDialog}
        handleClose={() => setOpenDeleteDialog(false)}
        handleSubmit={() => {
          const idArray = rowSelectionModel;
          deleteStudents(idArray)
            .then(() => {
              // 選択された行のIDに基づいて行を一括削除
              setStudents(
                students.filter((row) => !rowSelectionModel.includes(row.id))
              );
              setRowSelectionModel([]); // 選択状態をクリア
              setOpenDeleteDialog(false);
            })
            .catch((error) => {
              console.log(error);
            });
        }}
      />
      <MessageDialog
        title={t("common.notice")}
        message={noticeMessage}
        open={openNoticeDialog}
        actionText={t("common.confirm")}
        handleClose={() => {
          setOpenNoticeDialog(false);
        }}
      />

      <ProgressBackdropDialog open={deleteLoading} />
    </>
  );
};

export default Page;
